'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { testIds } from '@/root/shared-consts'
import { useGetAuthToken } from '@/root/shared-hooks/useGetAuthToken/useGetAuthToken'
import { useGetProfileData } from '@/root/shared-hooks/useGetProfileData/useGetProfileData'
import { useGetTranslations } from '@/root/shared-hooks'
import type { CustomerType, RoleOption } from '@/root/modules/shared/ConfirmPage/types'
import { useDefaultCountry } from '@/root/modules/shared/ConfirmPage/hooks/useDefaultCountry'

export const useRoleSelector = () => {
  const { isAuth } = useGetAuthToken()
  const { user, companies, isLoading, customerId, userId } = useGetProfileData(isAuth)
  const { getTranslation } = useGetTranslations()
  const { defaultCountryCode, defaultIntlCode } = useDefaultCountry()

  const [selectedRole, setSelectedRole] = useState<string>('private')
  const hasInitialized = useRef(false)

  const roleOptions: RoleOption[] = useMemo(() => {
    if (!isAuth || !user) return []

    const privateOption: RoleOption = {
      code: 'private',
      label: `${user.firstName} ${user.lastName} ${getTranslation('confirmationChooseRolePrivatePersonText')}`,
      type: 'private',
      testId: testIds.confirm.selectRolePrivate,
    }

    const companyOptions: RoleOption[] = companies
      .filter((company) => !company.creditCompany || company.enabled || company.manager)
      .map((company, index) => ({
        code: `company-${company.companyId ?? index}`,
        label: company.creditCompany
          ? `${company.companyName} ${getTranslation('confirmationChooseRoleCreditCompanyText')}`
          : `${company.companyName} ${getTranslation('confirmationChooseRoleCompanyText')}`,
        type: 'company',
        testId: testIds.confirm.selectRoleCompany,
        companyIndex: companies.indexOf(company),
      }))

    return [privateOption, ...companyOptions]
  }, [isAuth, user, companies, getTranslation])

  // Auto-select first option once when data first loads
  useEffect(() => {
    if (roleOptions.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true
      setSelectedRole(roleOptions[0].code)
    }
  }, [roleOptions])

  const selectedRoleOption = useMemo(
    () => roleOptions.find((option) => option.code === selectedRole),
    [roleOptions, selectedRole]
  )

  const selectedCompany = selectedRoleOption?.companyIndex != null ? companies[selectedRoleOption.companyIndex] : null

  const isCreditCompanySelected = selectedCompany?.creditCompany === true

  const creditBalance =
    isCreditCompanySelected && selectedCompany ? selectedCompany.creditLimit - selectedCompany.used : 0

  const getRoleFormData = useCallback(
    (roleCode: string) => {
      if (!user) return null

      const option = roleOptions.find((o) => o.code === roleCode)
      if (!option) return null

      const rawIntlCode = user.phone?.split(' ')[0] || ''
      const intlCode = rawIntlCode.startsWith('+') ? rawIntlCode : rawIntlCode ? `+${rawIntlCode}` : defaultIntlCode

      const personData = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        intlCode,
        phoneNumber: user.phone?.split(' ')[1] || '',
      }

      if (option.type === 'company' && option.companyIndex != null) {
        const company = companies[option.companyIndex]
        return {
          personData,
          companyData: company
            ? {
                legalName: company.companyName,
                registrationCode: company.regNumber,
                legalAddress: company.legalAddress ?? '',
                zip: company.zip ?? '',
                city: company.city ?? '',
                country: company.country ?? defaultCountryCode,
              }
            : null,
          companyId: company?.companyId,
          isCreditCompany: company?.creditCompany === true,
          customerType: 'company' as CustomerType,
        }
      }

      return {
        personData,
        companyData: null,
        companyId: undefined,
        isCreditCompany: false,
        customerType: 'private' as CustomerType,
      }
    },
    [user, companies, roleOptions, defaultCountryCode, defaultIntlCode]
  )

  return {
    isAuth,
    isProfileLoading: isLoading,
    roleOptions,
    selectedRole,
    setSelectedRole,
    getRoleFormData,
    isCreditCompanySelected,
    creditBalance,
    userId,
    customerId,
    selectedCompanyId: selectedCompany?.companyId,
  }
}
